import type { LoginResponse, MeResponse, StaffUser } from '@thesuccesstime/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, session } from '../api/client';

type AuthState = { status: 'loading' } | { status: 'signedOut' } | { status: 'signedIn'; user: StaffUser };

type Auth = {
  state: AuthState;
  user: StaffUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Signs in with a token from accept-invite / reset-password. */
  adopt: (accessToken: string, user?: StaffUser) => Promise<void>;
  reloadUser: () => Promise<void>;
  /** A UX hint only — the API checks every permission itself (BACKOFFICE_PLAN §3). */
  can: (...permissions: string[]) => boolean;
  canAny: (...permissions: string[]) => boolean;
};

const AuthContext = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const queryClient = useQueryClient();

  const loadMe = useCallback(async () => {
    const me = await api<MeResponse>('/admin/me');
    setState({ status: 'signedIn', user: me.data });
  }, []);

  useEffect(() => {
    session.onSignedOut(() => {
      queryClient.clear();
      setState({ status: 'signedOut' });
    });
    // A reload: the refresh cookie gets a new access token, if the session is still alive.
    void session.refresh().then(ok => (ok ? loadMe().catch(() => setState({ status: 'signedOut' })) : setState({ status: 'signedOut' })));
  }, [loadMe, queryClient]);

  const value = useMemo<Auth>(() => {
    const user = state.status === 'signedIn' ? state.user : null;
    const has = (permission: string) => user?.permissions.includes(permission) ?? false;
    return {
      state,
      user,
      login: async (email, password) => {
        const res = await api<LoginResponse>('/auth/login', { method: 'POST', body: { email, password } });
        session.set(res.data.accessToken);
        setState({ status: 'signedIn', user: res.data.user });
      },
      logout: async () => {
        await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
        session.clear();
        queryClient.clear();
        setState({ status: 'signedOut' });
      },
      adopt: async (accessToken, known) => {
        session.set(accessToken);
        if (known) setState({ status: 'signedIn', user: known });
        else await loadMe();
      },
      reloadUser: loadMe,
      can: (...permissions) => permissions.every(has),
      canAny: (...permissions) => permissions.some(has),
    };
  }, [state, loadMe, queryClient]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error('useAuth outside AuthProvider');
  return auth;
}
